import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Fade,
  Dialog,
  DialogContent,
  Chip,
  Avatar,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Message from './Message';
import OnboardingForm from './OnboardingForm';
import OTPVerification from './OTPVerification';
import MilesTimeSlotPicker from './MilesTimeSlotPicker';
import TradeAppraisalForm from './TradeAppraisalForm';
import FinanceLinkForm from './FinanceLinkForm';
import ContactRequestForm from './ContactRequestForm';
import MorePhotosForm from './MorePhotosForm';
import PersonIcon from '@mui/icons-material/Person';
import {
  MilesMessage,
  WidgetState,
  MessageType,
  WidgetConfig,
  FormId,
  TestDriveFormData,
  TradeAppraisalFormData,
  FinanceLinkFormData,
  ContactRequestFormData,
  MorePhotosFormData,
  VehicleContext,
  UserCoverImage,
  ViewType,
} from '../types';
import InfoDetails from './InfoDetails';
import { Vehicle } from './MilesCarousel';
import {
  containsDateTimeReference,
  isSimpleAffirmative,
} from '../utils/dateTimeDetector';
import { toast } from 'react-toastify';
import WidgetAPI from '../services/api';
import ForbiddenSubscriptionMessage from './ForbiddenSubscriptionMessage';
import { getIsWelcome, setIsWelcome } from '../utils/storage';
import WelcomeWebAppUserScreen from './WelcomeWebAppUserScreen';

export type NewLeadQuickOption =
  | 'schedule_test_drive'
  | 'request_finance'
  | 'price_and_payments'
  | 'warranty_and_history';

interface QuickOption {
  icon: string;
  title: string;
  shortTitle: string;
  code: NewLeadQuickOption;
  dialogTitle?: string;
  dialogDescription?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const quickOptions: QuickOption[] = [
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/ic_test_drive_new.png',
    title: 'schedule_test_drive',
    shortTitle: 'Test Drive',
    code: 'schedule_test_drive',
    dialogTitle: 'great_choice_lets_get_your_test_drive_scheduled',
    dialogDescription:
      'complete_the_information_below_and_we_will_confirm_your_test_drive_shortly',
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/ic_finance_request_new.png',
    title: 'request_finance',
    shortTitle: 'Finance',
    code: 'request_finance',
    dialogTitle: 'great_lets_help_you_get_the_best_term',
    dialogDescription:
      'complete_the_information_below_and_we_will_securely_send_you_the_finance_application_right_away',
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/ic_warranty_new.png',
    title: 'warranty_and_history',
    shortTitle: 'Warranty',
    code: 'warranty_and_history',
    dialogTitle: 'get_warranty_details',
    dialogDescription:
      'complete_the_information_below_and_a_sales_rep_will_be_in_touch_shortly',
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/ic_pricing_new.png',
    title: 'price_and_payments',
    shortTitle: 'Get E Price',
    code: 'price_and_payments',
    dialogTitle: 'get_pricing_and_payment_information',
    dialogDescription:
      'complete_the_information_below_and_a_sales_rep_will_be_in_touch_shortly',
  },
];

interface ChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
  currentView: WidgetState['currentView'];
  messages: MilesMessage[];
  onSendMessage: (
    message: string,
    metadata?: any,
    messageType?: MessageType
  ) => void;
  onBack: () => void;
  onSubmitOnboarding: (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    tcpaConsent: boolean;
  }) => Promise<void>;
  onVerifyOTP: (otp: string) => Promise<void>;
  isTestDriveRequest?: (message: string) => boolean;
  isSending: boolean;
  isLoading: boolean;
  isTyping: boolean;
  iconUrl: string;
  hasConversation: boolean;
  webUrl: string;
  config: WidgetConfig;
  api: WidgetAPI;
  testDriveRequestMessage?: string;
  activeForm: FormId | null;
  onCloseForm: () => void;
  onTestDriveSubmit: (data: TestDriveFormData) => Promise<void>;
  onTradeAppraisalSubmit: (data: TradeAppraisalFormData) => Promise<void>;
  onFinanceLinkSubmit: (data: FinanceLinkFormData) => Promise<void>;
  onContactRequestSubmit: (data: ContactRequestFormData) => Promise<void>;
  onMorePhotosSubmit: (data: MorePhotosFormData) => Promise<void>;
  vehicleContext?: VehicleContext;
  onAction?: (action: string) => void;
  isAuthenticated: boolean;
  isForbidden: boolean;
  onEnhanceClick: (currentVehicle?: Vehicle) => void;
  currentVehicle: Vehicle | null | undefined;
  userCoverImage?: UserCoverImage;
  handleChangeView: (view: ViewType) => void;
  onOpenCarTypeSelection: () => void;
  onVehicleClick: (currentVehicle?: Vehicle) => void;
  tradeFlowVehicleData?: any;
  updateLocalState: (newState: any) => void;
  isLiveAgentView: boolean;
  hasActiveSalesMan: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  isOpen,
  onClose,
  currentView,
  messages,
  onSendMessage,
  onBack,
  onSubmitOnboarding,
  onVerifyOTP,
  isTestDriveRequest,
  isSending,
  isLoading,
  isTyping,
  iconUrl,
  hasConversation,
  webUrl,
  config,
  api,
  testDriveRequestMessage,
  activeForm,
  onCloseForm,
  onTestDriveSubmit,
  onTradeAppraisalSubmit,
  onFinanceLinkSubmit,
  onContactRequestSubmit,
  onMorePhotosSubmit,
  vehicleContext,
  onAction,
  isAuthenticated,
  isForbidden,
  onEnhanceClick,
  currentVehicle,
  userCoverImage,
  handleChangeView,
  onOpenCarTypeSelection,
  onVehicleClick,
  tradeFlowVehicleData,
  updateLocalState,
  isLiveAgentView,
  hasActiveSalesMan,
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [pendingTestDriveMessage, setPendingTestDriveMessage] = useState('');

  const [isWelcomeStatus, setIsWelcomeStatus] = useState<boolean | null | undefined>(null);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  };

  useEffect(() => {
    scrollToBottom('smooth');
  }, [messages, isTyping]);

  // When widget is reopened or user navigates back to chat view,
  // ensure we start at the latest messages even if no new messages arrived.
  useEffect(() => {
    if (isOpen && currentView === 'chat') {
      scrollToBottom('auto');
    }
  }, [isOpen, currentView]);

  useEffect(() => {
    if (!isOpen) {
      setShowDateTimePicker(false);
      setPendingTestDriveMessage('');
    } else if (!isWelcomeStatus && isWelcomeStatus == null) {
      setIsWelcomeStatus(getIsWelcome());
    }


  }, [isOpen, isWelcomeStatus]);

  useEffect(() => {
    if (
      testDriveRequestMessage &&
      (testDriveRequestMessage || '').trim().length > 0 &&
      isAuthenticated
    ) {
      setPendingTestDriveMessage(testDriveRequestMessage);
      setShowDateTimePicker(true);
    } else if (testDriveRequestMessage && !isAuthenticated) {
      // User needs authentication but is not authenticated yet
      // Don't show time picker - let parent component handle onboarding
      setShowDateTimePicker(false);
    }
  }, [testDriveRequestMessage, isAuthenticated]);

  const handleSend = () => {
    if (inputMessage.trim() && !isSending) {
      const messageText = inputMessage.trim();

      // CRITICAL FIX: If time picker is already shown, block date/time text inputs
      if (showDateTimePicker) {
        // Allow simple affirmative responses to go through
        if (isSimpleAffirmative(messageText)) {
          onSendMessage(messageText, undefined, 'text');
          setInputMessage('');
          return;
        }

        // Block date/time inputs and show helpful message
        if (containsDateTimeReference(messageText)) {
          toast.info(
            'Please use the time picker above to select your appointment time'
          );
          setInputMessage('');
          return;
        }

        // For other messages while picker is shown, allow them through
        onSendMessage(messageText, undefined, 'text');
        setInputMessage('');
        return;
      }

      // All messages go to backend — server handles intent detection and flow control
      onSendMessage(messageText, undefined, 'text');
      setInputMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTimeSlotSelected = (datetimeString: string, label: string) => {
    const messageContent =
      pendingTestDriveMessage || 'I would like to book a test drive.';
    const fullMessage = `${messageContent} for ${label}`;

    onSendMessage(
      fullMessage,
      {
        appointmentDatetime: datetimeString,
        isAppointmentBooking: true,
      },
      'action'
    );

    setPendingTestDriveMessage('');
    setShowDateTimePicker(false);
  };

  const handleCancelDateTimePicker = () => {
    setShowDateTimePicker(false);
    setInputMessage(pendingTestDriveMessage);
    setPendingTestDriveMessage('');
  };


  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onAgencyWelcomeContinue = () => {
    setIsWelcomeStatus(false);
    setIsWelcome(true);

    if (!api.isShowEnhanceView() && currentView === 'enhance') {
      handleChangeView('chat');
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleQuickOptionClicked = (option?: QuickOption) => {
    let _message = '';
    switch (option?.code) {
      case 'schedule_test_drive':
        _message = 'I would like to book a test drive.';
        break;
      case 'request_finance':
        _message = 'I want to apply for financing.';
        break;
      case 'price_and_payments':
        _message = 'I want to know the E price.';
        break;
      case 'warranty_and_history':
        _message = 'I want to know the warranty.';
        break;
    }

    onSendMessage(_message, undefined, 'text');

  };

  const onLiveAgentClick = () => {
    let data: any = { isLiveAgentView: true };
    if (!isAuthenticated && hasActiveSalesMan) {
      data.currentView = 'onboard'
    } else {
      data.currentView = 'chat'
    }
    updateLocalState(data)
  }

  const onAskMileClick = () => {
    let data: any = { isLiveAgentView: false };
    if (isLiveAgentView) {
      data.currentView = 'chat'
    }
    updateLocalState(data)
  }


  const showBackButton = currentView === 'chat'; // || currentView === 'enhance';

  return (
    <Fade in={isOpen}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: { xs: 0, sm: 100 },
          right: { xs: 0, sm: 24 },
          left: { xs: 0, sm: 'auto' },
          top: { xs: 0, sm: 'auto' },
          width: { xs: '100%', sm: 400 },
          height: { xs: '100%', sm: 600 },
          maxHeight: { xs: '100vh', sm: 'calc(100vh - 120px)' },
          display: 'flex',
          flexDirection: 'column',
          zIndex: 2147483647,
          borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        {currentView !== 'forbidden' && (
          <AppBar position='static' elevation={0}>
            <Toolbar>
              {showBackButton && (
                <IconButton
                  edge='start'
                  color='inherit'
                  onClick={onBack}
                  aria-label='back'
                  sx={{ mr: 1 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Box
                component='img'
                src={iconUrl}
                alt='SAGE'
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  mr: 1,
                }}
              />
              <Box sx={{ maxWidth: "58%", flexGrow: 3, pr: 0.5, cursor: 'pointer' }}
                onClick={onAskMileClick}
              >
                <Typography variant='h6' component='div' noWrap>
                  {config.agentName}
                </Typography>
                <Chip
                  label={config.statusMessage}
                  size='small'
                  sx={{
                    height: 18,
                    fontSize: '0.7rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              </Box>
              <Box sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer'
              }}

                onClick={onLiveAgentClick}
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    p: 0.3,
                    animation: hasActiveSalesMan
                      ? 'blinkGreen 2s infinite'
                      : 'blinkRed 2s infinite',

                    '@keyframes blinkGreen': {
                      '0%': { backgroundColor: 'white' },
                      '50%': { backgroundColor: '#39e93f' }, // green
                      '100%': { backgroundColor: 'white' },
                    },

                    '@keyframes blinkRed': {
                      '0%': { backgroundColor: 'white' },
                      '50%': { backgroundColor: '#e53935' }, // red
                      '100%': { backgroundColor: 'white' },
                    },
                  }}
                >
                  <PersonIcon fontSize="small" sx={{
                    color: " #3C69EA "
                  }} />
                </Avatar>
                <Typography variant='h6' component='div' sx={{ pt: 0.5, fontSize: '0.9rem' }}>
                  Live Agent
                </Typography>
              </Box>
              <IconButton
                edge='end'
                color='inherit'
                onClick={onClose}
                aria-label='close'
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
        )}

        {/* Content Area */}
        {currentView === 'forbidden' && (
          <ForbiddenSubscriptionMessage onClose={onClose} />
        )}



        {isLiveAgentView && (isAuthenticated || !hasActiveSalesMan) ? <>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflowY: 'auto',
              background: `url('https://miles-cdn.qrbuddy.ai/static/img_miles_widget_bg.png')`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',

              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
              },
            }}
          >
            <Box
              component="img"
              src={iconUrl}
              alt="SAGE"
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                mb: 1,
              }}
            />

            <Typography
              variant="h5"
              component="div"
              sx={{
                fontSize: '1.2rem',
                textAlign: 'center',
                px: 2,
                color: 'white'
              }}
            >
              {hasActiveSalesMan ? 'A live agent will be in touch with you shortly.' : 'We\'re sorry but there is no live agent available at this time.'}
            </Typography>
            <Button
              sx={{ mt: 2 }}
              variant='contained'
              onClick={onAskMileClick}
            >Let's talk with SAGE</Button>
          </Box>
        </> : (<>
          {
            currentView === 'welcome' && <WelcomeWebAppUserScreen
              onSendMessage={(message: string) => {
                onSendMessage(message);
              }}
              isSending={isSending}
              dealerName={config.agentName}
              statusMessage={config.statusMessage}
              config={config}
              onEnhanceClick={onEnhanceClick}
            />
          }
          {/* 
        {currentView === 'welcome' && (
          api.checkIsWebAppUser() ? <WelcomeWebAppUserScreen 
            onSendMessage={(message: string) => {
              onSendMessage(message);
            }}
            isSending={isSending}
            dealerName={config.agentName}
            statusMessage={config.statusMessage}
            config={config}
            onEnhanceClick={onEnhanceClick}
          /> :
            <WelcomeScreen
              onSendMessage={(message: string) => {
                // if (isTestDriveRequest(message)) {
                //   setInputMessage(message);
                //   handleSend();
                // } else {
                onSendMessage(message);
                // }
              }}
              isSending={isSending}
              dealerName={config.agentName}
              statusMessage={config.statusMessage}
              config={config}
              onEnhanceClick={onEnhanceClick}
            />
        )} */}

          {currentView === 'onboard' && (
            <OnboardingForm
              onSubmit={onSubmitOnboarding}
              isLoading={isLoading}
              dealerName={config.agentName}
              onBack={onBack}
              isLiveAgentView={isLiveAgentView}
            />
          )}

          {currentView === 'otp' && (
            <OTPVerification
              onVerify={onVerifyOTP}
              onBack={onBack}
              isLoading={isLoading}
            />
          )}

          {currentView === 'enhance' && ( //  && !isWelcomeStatus
            <InfoDetails
              onBack={onBack}
              iconUrl={iconUrl}
              currentVehicle={currentVehicle}
              config={config}
              onSendMessage={onSendMessage}
              userCoverImage={userCoverImage}
              dealerName={config.agentName}
              onOpenCarTypeSelection={onOpenCarTypeSelection}
            />
          )}

          {/* {currentView === 'enhance' && isWelcomeStatus && (
          <AgencyWelcome
            continue={onAgencyWelcomeContinue}
            currentVehicle={currentVehicle}
            config={config}
            userCoverImage={userCoverImage}
          />
        )} */}


          {currentView === 'chat' && (
            <>
              <Box
                ref={messagesContainerRef}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  // background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
                  background: `url('https://miles-cdn.qrbuddy.ai/static/img_miles_widget_bg.png')`,
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  pt: 2,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background:
                      'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
                    borderRadius: '8px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background:
                      'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
                  },
                }}>

                {/* <Grid
              container
              spacing={0}
              gap={1}
              sx={{
                p: 1,
                justifyContent: 'center',
              }}
            >
              {quickOptions.map((item) => {
                  return (
                    <Grid size={2.7}>
                      <Card
                        sx={{
                          borderRadius: 2,
                          // boxShadow: 4,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 1,
                          height: '100%',
                          gap: 1,
                          textAlign: 'center',
                        }}
                        onClick={() => {
                          handleQuickOptionClicked(item);
                        }}
                      >
                        <Box
                          component={'img'}
                          src={item.icon}
                          alt={item.title}
                          sx={{ width: '40px', height: '40px' }}
                        />
                        <Typography
                        variant='caption'
                          sx={{
                            // fontSize: { xs: '0.6rem', sm: '1rem' },
                            lineHeight: 1.2,
                            textTransform: 'capitalize',
                            fontWeight: 600,
                          }}
                        >
                          {item.shortTitle}
                        </Typography>
                      </Card>
                    </Grid>
                  );
                })}
            </Grid> */}

                {/* Messages Container */}
                <Box
                // ref={messagesContainerRef}
                // sx={{
                //   flex: 1,
                //   overflowY: 'auto',
                //   // background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
                //   background: `url('https://miles-cdn.qrbuddy.ai/static/img_miles_widget_bg.png')`,
                //   backgroundSize: 'cover',
                //   backgroundRepeat: 'no-repeat',
                //   pt: 2,
                //   '&::-webkit-scrollbar': {
                //     width: '8px',
                //   },
                //   '&::-webkit-scrollbar-track': {
                //     backgroundColor: '#f1f1f1',
                //     borderRadius: '8px',
                //   },
                //   '&::-webkit-scrollbar-thumb': {
                //     background:
                //       'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
                //     borderRadius: '8px',
                //   },
                //   '&::-webkit-scrollbar-thumb:hover': {
                //     background:
                //       'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
                //   },
                // }}
                >
                  {isLoading && messages.length === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : messages.length === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        textAlign: 'center',
                        color: 'text.secondary',
                        px: 3,
                      }}
                    >
                      <Box
                        component='img'
                        src={iconUrl}
                        alt='SAGE'
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          mb: 2,
                          opacity: 0.5,
                        }}
                      />
                      <Typography variant='h6' gutterBottom>
                        Hi! I'm SAGE
                      </Typography>
                      <Typography variant='body2'>
                        I'm here to help you find the perfect property and answer any
                        questions you have.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {messages.map((message) => (
                        <Message
                          key={message.id}
                          message={message}
                          iconUrl={iconUrl}
                          webUrl={webUrl}
                          config={config}
                          onAction={onAction}
                          onEnhanceClick={onEnhanceClick}
                          onContentHeightChange={() => scrollToBottom('smooth')}
                          onVehicleClick={onVehicleClick} />
                      ))}
                      {isTyping && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                          }}
                        >
                          <Box
                            component='img'
                            src={iconUrl}
                            alt='SAGE'
                            sx={{ width: 32, height: 32, borderRadius: '50%' }}
                          />
                          <Paper
                            elevation={1}
                            sx={{
                              px: 1.5,
                              py: 1,
                              backgroundColor: '#8669fa',
                              color: '#fff',
                              borderRadius: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.5,
                                alignItems: 'center',
                                '@keyframes milesTyping': {
                                  '0%': {
                                    opacity: 0.2,
                                    transform: 'translateY(0px)',
                                  },
                                  '50%': {
                                    opacity: 1,
                                    transform: 'translateY(-2px)',
                                  },
                                  '100%': {
                                    opacity: 0.2,
                                    transform: 'translateY(0px)',
                                  },
                                },
                              }}
                            >
                              {[0, 1, 2].map((dot) => (
                                <Box
                                  key={dot}
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    animation: `milesTyping 1s ease-in-out ${dot * 0.15
                                      }s infinite`,
                                  }}
                                />
                              ))}
                            </Box>
                          </Paper>
                        </Box>
                      )}
                      <div className='lastMessage' ref={messagesEndRef} />
                    </>
                  )}
                </Box>
              </Box>

              {/* DateTime Picker Overlay */}
              {((showDateTimePicker && isAuthenticated) ||
                activeForm === 'form_test_drive') && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'background.paper',
                      borderTop: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon
                          sx={{ color: 'primary.main', fontSize: 20 }}
                        />
                        <Typography variant='subtitle2' fontWeight={600}>
                          Select your preferred time for your test drive
                        </Typography>
                      </Box>
                      <IconButton size='small' onClick={handleCancelDateTimePicker}>
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </Box>
                    <MilesTimeSlotPicker
                      onSelectTimeSlot={handleTimeSlotSelected}
                      config={config}
                      api={api}
                    />
                  </Box>
                )}

              {/* Input Area */}
              {/* Show input if:
                1. Time picker is not shown, OR
                2. Time picker is requested but user is not authenticated (prevent stuck state)
            */}
              {(!showDateTimePicker ||
                (showDateTimePicker && !isAuthenticated)) && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'background.paper',
                      borderTop: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder='Type your message...'
                      disabled={isSending}
                      variant='outlined'
                      size='small'
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              color='primary'
                              onClick={handleSend}
                              disabled={!inputMessage.trim() || isSending}
                              edge='end'
                            >
                              {isSending ? (
                                <CircularProgress size={24} />
                              ) : (
                                <SendIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                )}
            </>
          )}
        </>)}
        {/* Form Modals */}
        {/* <Dialog
          open={activeForm === 'form_test_drive'}
          onClose={onCloseForm}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={600}>
                Schedule Test Drive
              </Typography>
              <IconButton size="small" onClick={onCloseForm}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <MilesTimeSlotPicker
              onSelectTimeSlot={async (datetimeUTC: string, label: string) => {
                handleTimeSlotSelected(datetimeUTC, label);
                onCloseForm();
              }}
              config={config}
              api={api}
              // onSelectTimeSlot={async (datetimeUTC: string, label: string) => {
              //   await onTestDriveSubmit({
              //     firstName: '',
              //     lastName: '',
              //     phone: '',
              //     preferredDateTime: datetimeUTC,
              //     tcpaConsent: true,
              //   });
              //   onCloseForm();
              // }}
            />
          </DialogContent>
        </Dialog> */}

        <Dialog
          open={activeForm === 'form_trade_appraisal'}
          onClose={onCloseForm}
          maxWidth='sm'
          fullWidth
        >
          <DialogContent>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='h6' fontWeight={600}>
                Trade-In Appraisal
              </Typography>
              <IconButton size='small' onClick={onCloseForm}>
                <CloseIcon fontSize='small' />
              </IconButton>
            </Box>
            <TradeAppraisalForm
              onSubmit={onTradeAppraisalSubmit}
              isLoading={isLoading}
              dealerName={config.agentName}
              initialData={tradeFlowVehicleData || undefined}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={activeForm === 'form_finance_link'}
          onClose={onCloseForm}
          maxWidth='sm'
          fullWidth
        >
          <DialogContent>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='h6' fontWeight={600}>
                Finance Application
              </Typography>
              <IconButton size='small' onClick={onCloseForm}>
                <CloseIcon fontSize='small' />
              </IconButton>
            </Box>
            <FinanceLinkForm
              onSubmit={onFinanceLinkSubmit}
              isLoading={isLoading}
              dealerName={config.agentName}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={activeForm === 'form_contact_request'}
          onClose={onCloseForm}
          maxWidth='sm'
          fullWidth
        >
          <DialogContent>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='h6' fontWeight={600}>
                Contact Request
              </Typography>
              <IconButton size='small' onClick={onCloseForm}>
                <CloseIcon fontSize='small' />
              </IconButton>
            </Box>
            <ContactRequestForm
              onSubmit={onContactRequestSubmit}
              isLoading={isLoading}
              dealerName={config.agentName}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={activeForm === 'form_more_photos'}
          onClose={onCloseForm}
          maxWidth='sm'
          fullWidth
        >
          <DialogContent>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='h6' fontWeight={600}>
                Request More Photos
              </Typography>
              <IconButton size='small' onClick={onCloseForm}>
                <CloseIcon fontSize='small' />
              </IconButton>
            </Box>
            <MorePhotosForm
              onSubmit={onMorePhotosSubmit}
              isLoading={isLoading}
              dealerName={config.agentName}
              vehicleContext={vehicleContext}
            />
          </DialogContent>
        </Dialog>
      </Paper>
    </Fade>
  );
};

export default ChatWindow;
